import { getAppBridge } from './app-bridge.js';

/**
 * Thin authenticated fetch wrapper. Adds the App Bridge JWT on every call.
 *
 * Error handling philosophy:
 * - 401 → throw `SessionExpiredError` so the root layout can swap to a
 *   reconnect banner + top-level redirect to /api/auth. Do NOT retry.
 * - 402 → throw `PaywallError` with the server-provided plan info so the
 *   caller can show an upgrade CTA.
 * - 409 → thrown as `ConflictError` (e.g. "scan already in progress").
 * - other non-2xx → generic `ApiError` with the server message.
 *
 * Responses are typed only at call sites — we return `unknown` here so we
 * don't lie about runtime shape.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('session_expired');
    this.name = 'SessionExpiredError';
  }
}

export class PaywallError extends Error {
  constructor(
    message: string,
    public requiresPlan?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'PaywallError';
  }
}

export class ConflictError extends Error {
  constructor(
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ConflictError';
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const app = await getAppBridge();
  const token = await app.idToken();

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(opts.body != null ? { 'Content-Type': 'application/json' } : {}),
    },
    body: opts.body != null ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401) throw new SessionExpiredError();

  const contentType = res.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await res.json() : await res.text();

  if (res.ok) return parsed as T;

  if (res.status === 402) {
    const p = parsed as { requiresPlan?: string; error?: string };
    throw new PaywallError(p.error ?? 'upgrade_required', p.requiresPlan, parsed);
  }
  if (res.status === 409) {
    const p = parsed as { error?: string };
    throw new ConflictError(p.error ?? 'conflict', parsed);
  }
  const p = (parsed ?? {}) as { error?: string };
  throw new ApiError(p.error ?? `http_${res.status}`, res.status, parsed);
}

/**
 * Authenticated file download. We can't just do `window.location = url`
 * because top-level navigations don't carry the App Bridge JWT header —
 * the middleware would 401 with `missing_session_token`.
 *
 * Instead: fetch the file with the JWT, turn the response into a Blob, and
 * trigger the browser's native download via a temporary anchor. Works
 * inside the Shopify embedded iframe (no popup blocker issues — it's
 * user-initiated and same-origin).
 */
async function download(path: string, fallbackFilename: string): Promise<void> {
  const app = await getAppBridge();
  const token = await app.idToken();

  const res = await fetch(path, { headers: { Authorization: `Bearer ${token}` } });

  if (res.status === 401) throw new SessionExpiredError();

  if (!res.ok) {
    // Error responses are JSON; parse and surface the server's error code.
    const parsed = await res.json().catch(() => ({}));
    if (res.status === 402) {
      const p = parsed as { requiresPlan?: string; error?: string };
      throw new PaywallError(p.error ?? 'upgrade_required', p.requiresPlan, parsed);
    }
    const p = (parsed ?? {}) as { error?: string };
    throw new ApiError(p.error ?? `http_${res.status}`, res.status, parsed);
  }

  // Prefer the server-provided filename from Content-Disposition.
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename="?([^"]+)"?/u.exec(cd);
  const filename = m?.[1] ?? fallbackFilename;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PUT', body, signal }),
  download,
};
